/* Reusable independent reveal / scene controls, including theme-editor reloads. */
(() => {
  if (window.BirdyStory) return;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const roots = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  function initReveal(root) {
    if (root.dataset.birdyInitialized) return;
    const range = root.querySelector('[data-compare-range]');
    const stage = root.querySelector('[data-compare-stage]');
    if (!range || !stage) return;
    root.dataset.birdyInitialized = 'true';
    const controls = [...root.querySelectorAll('[data-compare-value]')];
    function update(value) {
      const next = Math.min(100, Math.max(0, Number(value)));
      range.value = String(next);
      stage.style.setProperty('--by-split', `${next}%`);
      const mode = next === 100 ? 'before' : next === 0 ? 'after' : 'split';
      stage.dataset.mode = mode;
      range.setAttribute('aria-valuetext', `${next}% natural view, ${100 - next}% illustrated Birdy View`);
      controls.forEach(button => button.setAttribute('aria-pressed', String(mode === (button.dataset.compareValue === '100' ? 'before' : button.dataset.compareValue === '0' ? 'after' : 'split'))));
    }
    range.addEventListener('input', () => update(range.value));
    controls.forEach(button => button.addEventListener('click', () => update(button.dataset.compareValue)));
    const zoomButtons = [...root.querySelectorAll('[data-compare-zoom]')];
    zoomButtons.forEach(button => button.addEventListener('click', () => {
      stage.dataset.zoom = button.dataset.compareZoom;
      zoomButtons.forEach(option => option.setAttribute('aria-pressed', String(option === button)));
    }));
    update(range.value);
  }
  function initLenses(root) {
    if (root.dataset.birdyInitialized) return;
    const track = root.querySelector('[data-lens-track]');
    const slides = [...root.querySelectorAll('[data-lens-slide]')];
    if (!track || !slides.length) return;
    root.dataset.birdyInitialized = 'true';
    const controls = [...root.querySelectorAll('[data-lens-index]')];
    const dialog = root.querySelector('[data-lens-dialog]');
    let selected = 0;
    let frame = 0;
    let returnFocus = null;
    function setSelected(index) {
      selected = Math.min(slides.length - 1, Math.max(0, index));
      controls.forEach((button, i) => button.setAttribute('aria-pressed', String(i === selected)));
    }
    function goTo(index) {
      setSelected(index);
      // offsetLeft is unsafe when the track is not the offsetParent.
      const target = slides[selected].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      track.scrollTo({ left: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    controls.forEach(button => button.addEventListener('click', () => goTo(Number(button.dataset.lensIndex))));
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const left = track.getBoundingClientRect().left;
        let closest = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const next = Math.abs(slide.getBoundingClientRect().left - left);
          if (next < distance) { closest = index; distance = next; }
        });
        setSelected(closest);
      });
    }, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      goTo(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : selected + (event.key === 'ArrowRight' ? 1 : -1));
    });
    if (dialog) {
      const enlarged = dialog.querySelector('[data-lens-enlarged]');
      const caption = dialog.querySelector('[data-lens-caption]');
      root.querySelectorAll('[data-lens-expand]').forEach(button => button.addEventListener('click', () => {
        const slide = slides[Number(button.dataset.lensExpand)];
        const image = slide?.querySelector('img');
        if (!image || !enlarged) return;
        enlarged.src = image.currentSrc || image.src;
        enlarged.alt = image.alt;
        if (caption) caption.textContent = slide.querySelector('figcaption span')?.textContent || '';
        returnFocus = button;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else window.open(enlarged.src, '_blank', 'noopener');
      }));
      dialog.querySelector('[data-lens-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => { if (returnFocus?.isConnected) returnFocus.focus(); });
    }
    root.addEventListener('shopify:block:select', event => {
      const block = event.target.closest('[data-lens-slide]');
      const index = slides.indexOf(block);
      if (index >= 0) goTo(index);
    });
  }
  function init(scope = document) {
    roots(scope, '[data-birdy-compare]').forEach(initReveal);
    roots(scope, '[data-birdy-lenses]').forEach(initLenses);
  }
  window.BirdyStory = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
